import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'

import styles from './styles.module.scss'

import config from '../../../../config'

import { UploadIcon } from '../../Icons'
import Input from '../../../Core/Input/Input'

const BASE_URL = config.api.url

const isUrl = (value) => {
  try {
    const url = new URL(value)

    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

const AssetPhotoUpload = forwardRef(({companyId}, ref) => {
  const { t } = useTranslation()

  const [url, setUrl] = useState('')
  const [file, setFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const inputFileRef = useRef(null)

  useImperativeHandle(ref, () => ({
    getFile: () => file,
    setFile: (url) => {
      handleUrl(url)
    },
    reset: () => {
      setPreviewUrl(null)
      setFile(null)
      setUrl('')
      setError(null)
    }
  }))

  const handleFileChange = (event) => {
    const file = event.target.files[0]

    if (!file) {
      return
    }

    if (!file.type.startsWith('image/')) {
      setError('The selected file is not an image')
      return
    }

    setError(null)
    setFile(file)

    const objectUrl = URL.createObjectURL(file)
    setPreviewUrl(objectUrl)
  }

  const handleUrl = async (url) => {
    setLoading(true)
    setError(null)

    const token = localStorage.getItem('token')

    try {
      const response = await fetch(
        `${BASE_URL}/company/${companyId}/assets/fetch-image?url=${encodeURIComponent(url)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      )

      if (!response.ok) {
        throw new Error('Unable to load image')
      }

      const blob = await response.blob()

      const objectUrl = URL.createObjectURL(blob)

      setPreviewUrl(objectUrl)

      const file = new File(
        [blob],
        'image',
        { type: blob.type }
      )

      setFile(file)
    } catch (error) {
      console.error(error)
      setPreviewUrl(null)
      setFile(null)
      setError('Unable to load image')
    } finally {
      setLoading(false)
    }
  }

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  return (
    <div className={styles.container}>
      <div className={styles.preview}>
        {loading && <div>Loading...</div>}

        {previewUrl && !loading && (
          <img
            src={previewUrl}
            alt="Preview"
            className={styles.previewImage}
          />
        )}

        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}
      </div>

      <div className={styles.upload}>
        <Input
          type="text"
          placeholder={t('https://...')}
          value={url}
          onPaste={(event) => {
            const text = event.clipboardData.getData('text').trim()
            if (isUrl(text)) {
              setUrl(text)
              handleUrl(text)
            }
          }}
          onChange={(event) => {
            setUrl(event.target.value)
          }}
          fullWidth
        />
        <input
          type="file"
          className={styles.inputFile}
          onChange={handleFileChange}
          ref={inputFileRef}
          accept="image/*"
        />
        <div className={styles.buttons}>
          <button
            type="button"
            className={styles.uploadButton}
            onClick={() => inputFileRef.current.click()}
          >
            <UploadIcon />
            {t('Upload photo')}
          </button>
          {
            file || url
              ? <button type="button" className={styles.clearButton} onClick={() => {
                setFile(null)
                setUrl('')
                setPreviewUrl(null)
                setError(null)
              }}>
                {t('Clear')}
                </button>
              : null
          }
          </div>
      </div>
    </div>
  )
})

export default AssetPhotoUpload